import { useState, useCallback, useEffect } from 'react'
import PropTypes from 'prop-types'
import {
  Row,
  Col,
  InputGroup,
  InputGroupAddon,
  InputGroupText,
  Input,
  Modal,
  ModalHeader,
  ModalBody
} from 'reactstrap'
import Fuzzy from 'fuzzy'
import Favorite from '~/components/shared/Favorite'

function QuicklinksModalHeader ({ title, toggleFunction }) {
  const close = (
    <div className='close cursor-pointer' onClick={toggleFunction}>
      <i className='fa fa-close' />
    </div>
  )

  return (
    <ModalHeader toggle={toggleFunction} close={close}>{title}</ModalHeader>
  )
}

QuicklinksModalHeader.propTypes = {
  title: PropTypes.string.isRequired,
  toggleFunction: PropTypes.func.isRequired
}

function FilterInput ({ onFilter }) {
  const handleChange = useCallback(event => onFilter(event.target.value), [])

  return (
    <InputGroup>
      <InputGroupAddon addonType='prepend'>
        <InputGroupText className='bg-white pr-1'>
          <i className='fa fa-search fa-lg' />
        </InputGroupText>
      </InputGroupAddon>
      <Input
        type='text'
        placeholder='Search'
        onChange={handleChange}
      />
    </InputGroup>
  )
}

FilterInput.propTypes = {
  onFilter: PropTypes.func.isRequired
}

function Application ({ app, favorite }) {
  const objectType = 'APPLICATION'
  const objectId = app.id
  const isFavorited = favorite.favorited

  return (
    <Row className='text-center py-1'>
      <Col xs='2'>
        <a href={app.embedUrl} alt={app.name} target='_blank' rel='noopener noreferrer'>
          <img className='quicklinks-image mw-100' src={app.logo} alt={app.name} />
        </a>
      </Col>
      <Col className='text-left'>
        <a className='pt-2 dark-link' href={app.embedUrl} alt={app.name} target='_blank' rel='noopener noreferrer'>{app.name}</a>
      </Col>
      <Col xs='auto'>
        <Favorite isFavorited={isFavorited} objectType={objectType} objectId={objectId} />
      </Col>
    </Row>
  )
}

Application.propTypes = {
  app: PropTypes.object.isRequired,
  favorite: PropTypes.object
}

function QuicklinksModal ({ quicklinksModalOpen, toggleQuickLinksModal, profile }) {
  const [filter, setFilter] = useState('')
  let nonfavoriteResult = null
  let favoriteResult = null

  useEffect(() => {
    setFilter('')
  }, [quicklinksModalOpen])

  if (profile.user.applications.length === 0) {
    nonfavoriteResult = <h3>No applications...</h3>
    favoriteResult = <h3>No applications...</h3>
  } else {
    const { applications } = profile.user
    const filteredApplications = filter.length > 0 ? Fuzzy.filter(filter, applications, { extract: p => p.name }).map(r => r.original) : applications
    favoriteResult = []
    nonfavoriteResult = []

    for (const app of filteredApplications) {
      const appResult = <Application key={app.id} app={app} favorite={app.favorite} />

      if (app.favorite.favorited) {
        favoriteResult.push(appResult)
      } else {
        nonfavoriteResult.push(appResult)
      }
    }

    if (favoriteResult.length === 0) {
      favoriteResult = <div className='px-3'>No favorite links</div>
    }

    if (nonfavoriteResult.length === 0) {
      nonfavoriteResult = <div className='px-3'>No links</div>
    }
  }

  return (
    <Modal
      isOpen={quicklinksModalOpen}
      toggle={toggleQuickLinksModal}
      modalClassName='quicklinks-modal'
      modalTransition={{ timeout: 0 }}
      backdropTransition={{ timeout: 0 }}
    >
      <QuicklinksModalHeader title='Quick Links' toggleFunction={toggleQuickLinksModal} />
      <div className='quicklinks-filter'>
        <ModalBody>
          <h3>Filter Links</h3>
          <FilterInput onFilter={setFilter} />
        </ModalBody>
      </div>
      <ModalBody>
        <Row>
          <Col>
            <h4 className='text-primary'>FAVORITE LINKS</h4>
          </Col>
        </Row>
        <Row className='pb-4'>
          <Col>
            {favoriteResult}
          </Col>
        </Row>
        <Row>
          <Col>
            <h4>LINKS</h4>
          </Col>
        </Row>
        <Row>
          <Col>
            {nonfavoriteResult}
          </Col>
        </Row>
      </ModalBody>
    </Modal>
  )
}

QuicklinksModal.propTypes = {
  quicklinksModalOpen: PropTypes.bool.isRequired,
  toggleQuickLinksModal: PropTypes.func.isRequired,
  profile: PropTypes.object
}

export default QuicklinksModal
